/**
 * RouteFields: the outside model routes one benchmark's adapter opens, as
 * its descriptor declares them (`BenchmarkAdapterItem.routes`). A `caller`
 * route gets a model input under its fixed name. An `application` route
 * gets none, because the adapter picks that model itself and the night
 * refuses a model saved for it (TRA-1503). A stored route the adapter does
 * not take is listed with the reason and a Remove action, so a row saved
 * before this form can be put right here.
 */

import { X } from 'lucide-react'

import type { BenchmarkAdapterRoute } from '@/api/generated/model'
import { RowAction } from '@/components/settings/RowAction'
import { EYEBROW } from '@/components/ui/eyebrow'

import type { PairRow } from './benchmarkSources'
import { TextField } from './TextField'

interface RouteFieldsProps {
  /** Prefix for the inputs' ids, unique on the page. */
  id: string
  routes: PairRow[]
  /** The picked adapter's routes, or undefined when no shipped adapter is picked. */
  declared: readonly BenchmarkAdapterRoute[] | undefined
  onChange: (next: (previous: PairRow[]) => PairRow[]) => void
}

export function RouteFields({ id, routes, declared, onChange }: RouteFieldsProps): React.JSX.Element {
  const callers = (declared ?? []).filter((route) => route.model_source === 'caller')
  const own = (declared ?? []).filter((route) => route.model_source === 'application')
  const stray = routes.filter(
    (row) => row.key.trim() !== '' && !callers.some((route) => route.name === row.key.trim()),
  )
  const setModel = (name: string, value: string): void => {
    onChange((previous) =>
      previous.some((row) => row.key === name)
        ? previous.map((row) => (row.key === name ? { ...row, value } : row))
        : [...previous, { key: name, value }],
    )
  }
  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <p className={EYEBROW}>Routes</p>
        <p className="text-xs text-muted-foreground">{routesHint(declared)}</p>
      </div>
      {callers.map((route) => (
        <TextField
          key={route.name}
          id={`${id}-route-${route.name}`}
          label={`Model for the ${route.name} route`}
          value={routes.find((row) => row.key === route.name)?.value ?? ''}
          mono
          placeholder="google/gemini-2.5-flash"
          onChange={(value) => {
            setModel(route.name, value)
          }}
        />
      ))}
      {own.length > 0 && (
        <ul className="space-y-1">
          {own.map((route) => (
            <li key={route.name} className="text-sm">
              <span className="font-mono text-xs">{route.name}</span>
              <span className="ml-2 text-muted-foreground">The adapter picks this model.</span>
            </li>
          ))}
        </ul>
      )}
      {stray.length > 0 && (
        <ul className="space-y-2">
          {stray.map((row) => (
            <li key={row.key} className="flex items-center gap-2 text-sm">
              <span className="break-all font-mono text-xs">
                {row.key} {row.value}
              </span>
              <span className="text-muted-foreground">{strayReason(row.key, declared)}</span>
              <span className="ml-auto">
                <RowAction
                  label={`Remove the ${row.key} route`}
                  icon={X}
                  destructive
                  onClick={() => {
                    onChange((previous) => previous.filter((pair) => pair.key !== row.key))
                  }}
                />
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function routesHint(declared: readonly BenchmarkAdapterRoute[] | undefined): string {
  if (declared === undefined) return 'Pick a shipped adapter to see the routes it opens.'
  if (declared.length === 0) return 'This adapter opens no outside model route.'
  return 'These are the outside model routes the adapter opens.'
}

function strayReason(key: string, declared: readonly BenchmarkAdapterRoute[] | undefined): string {
  if (declared === undefined) return 'The platform does not ship this adapter.'
  return declared.some((route) => route.name === key.trim())
    ? 'The adapter picks this model itself.'
    : 'The adapter does not open this route.'
}
